import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaPlus, FaSave, FaTimes, FaEdit, FaTrash, FaLink } from 'react-icons/fa';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

const { API_BASE_URL } = config;

function numeroParaReais(valor) {
  if (isNaN(valor) || valor === null) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
const CadastroServico = () => {
  const [servicos, setServicos] = useState([]);
  const [servicosPassivos, setServicosPassivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buscaServico, setBuscaServico] = useState('');
  const [buscaPassivo, setBuscaPassivo] = useState('');
  const [buscaNaoVeicular, setBuscaNaoVeicular] = useState('');

  // Estados dos modais
  const [modalServico, setModalServico] = useState({ open: false, servico: null });
  const [modalPassivo, setModalPassivo] = useState({ open: false, passivo: null });
  const [modalVincular, setModalVincular] = useState({ open: false, servico: null });

  // ── NOVO: carrega todos os serviços principais (sem filtro) para a listagem
  const carregarServicos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      const response = await axios.get(`${API_BASE_URL}/servicos`, {
        params: { descricao: '' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setServicos(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar serviços:', err);
      setError(err.response?.data?.message || 'Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const carregarServicosPassivos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      const response = await axios.get(`${API_BASE_URL}/servicoPassivos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServicosPassivos(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar serviços passivos:', err);
      setError(err.response?.data?.message || 'Erro ao carregar serviços passivos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarServicos();
    carregarServicosPassivos();
  }, []);

  // ── Serviços principais são divididos em duas seções: veiculares e não veiculares
  const servicosVeiculoFiltrados = servicos.filter(s =>
    !s.naoRelacionadoVeiculo && s.descricao?.toLowerCase().includes(buscaServico.toLowerCase())
  );
  const servicosNaoVeiculoFiltrados = servicos.filter(s =>
    s.naoRelacionadoVeiculo && s.descricao?.toLowerCase().includes(buscaNaoVeicular.toLowerCase())
  );
  const passivosFiltrados = servicosPassivos.filter(p =>
    p.descricao?.toLowerCase().includes(buscaPassivo.toLowerCase())
  );

  // ── Callbacks dos modais: atualizam a lista local sem precisar refazer o GET
  const handleServicoSalvo = (servicoSalvo) => {
    setServicos(prev => {
      const existe = prev.some(s => s.idServico === servicoSalvo.idServico);
      return existe
        ? prev.map(s => (s.idServico === servicoSalvo.idServico ? servicoSalvo : s))
        : [...prev, servicoSalvo];
    });
    setModalServico({ open: false, servico: null });
  };

  const handleServicoExcluido = (idServico) => {
    setServicos(prev => prev.filter(s => s.idServico !== idServico));
    setModalServico({ open: false, servico: null });
  };

  const handlePassivoSalvo = (passivoSalvo) => {
    setServicosPassivos(prev => {
      const existe = prev.some(p => p.idServicoPassivo === passivoSalvo.idServicoPassivo);
      return existe
        ? prev.map(p => (p.idServicoPassivo === passivoSalvo.idServicoPassivo ? passivoSalvo : p))
        : [...prev, passivoSalvo];
    });
    setModalPassivo({ open: false, passivo: null });
  };

  const handlePassivoExcluido = (idServicoPassivo) => {
    setServicosPassivos(prev => prev.filter(p => p.idServicoPassivo !== idServicoPassivo));
    setModalPassivo({ open: false, passivo: null });
  };

  const handleVinculosSalvos = (servicoAtualizado) => {
    setServicos(prev =>
      prev.map(s => (s.idServico === servicoAtualizado.idServico ? servicoAtualizado : s))
    );
    setModalVincular({ open: false, servico: null });
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Cadastro de Serviço</h1>
        </div>

        {error && <div style={styles.errorMessage}>{error}</div>}

        <div style={styles.colunas}>
          {/* ══════════ CARD: SERVIÇOS PRINCIPAIS VEÍCULOS (HONORÁRIOS) ══════════ */}
          <ColunaServicosPrincipais
            titulo="Serviços Principais Veículos (Honorários)"
            servicos={servicosVeiculoFiltrados}
            busca={buscaServico}
            onBuscaChange={setBuscaServico}
            placeholderBusca="Buscar serviço..."
            loading={loading}
            mensagemVazia="Nenhum serviço encontrado"
            onEditar={(s) => setModalServico({ open: true, servico: s })}
            onVincular={(s) => setModalVincular({ open: true, servico: s })}
            onAdicionar={() => setModalServico({ open: true, servico: null, defaultNaoRelacionado: false })}
            labelBotaoAdicionar="Serviço"
          />

          {/* ══════════ CARD: SERVIÇOS PASSIVOS (ao lado, ocupando as duas linhas) ══════════ */}
          <div style={{ ...styles.coluna, ...styles.colunaPassivos }}>
            <h2 style={styles.colunaTitulo}>Serviços Passivos</h2>

            <div style={styles.buscaBox}>
              <FaSearch style={styles.buscaIcone} />
              <input
                type="text"
                value={buscaPassivo}
                onChange={(e) => setBuscaPassivo(e.target.value)}
                placeholder="Buscar serviço passivo..."
                style={styles.buscaInput}
              />
            </div>

            <div style={styles.lista}>
              {loading && servicosPassivos.length === 0 ? (
                <p style={styles.loading}>Carregando...</p>
              ) : passivosFiltrados.length > 0 ? (
                passivosFiltrados.map(p => (
                  <div key={p.idServicoPassivo} style={styles.item}>
                    <div style={styles.itemInfo}>
                      <span style={styles.itemDescricao}>{p.descricao}</span>
                      <span style={styles.itemValor}>
                        {p.valorDigitavel ? 'Digitável' : numeroParaReais(p.valor)}
                      </span>
                    </div>
                    <div style={styles.itemAcoes}>
                      <button
                        title="Editar serviço passivo"
                        onClick={() => setModalPassivo({ open: true, passivo: p })}
                        style={styles.iconButton}
                      >
                        <FaEdit />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p style={styles.noResults}>Nenhum serviço passivo encontrado</p>
              )}
            </div>

            <button
              onClick={() => setModalPassivo({ open: true, passivo: null })}
              style={styles.addButton}
            >
              <FaPlus /> Serviço Passivo
            </button>
          </div>

          {/* ══════════ CARD: SERVIÇOS PRINCIPAIS NÃO RELACIONADOS A VEÍCULO (abaixo do de veículos) ══════════ */}
          <ColunaServicosPrincipais
            titulo="Serviços Principais Não Relacionados a Veículo"
            servicos={servicosNaoVeiculoFiltrados}
            busca={buscaNaoVeicular}
            onBuscaChange={setBuscaNaoVeicular}
            placeholderBusca="Buscar serviço..."
            loading={loading}
            mensagemVazia="Nenhum serviço não veicular encontrado"
            onEditar={(s) => setModalServico({ open: true, servico: s })}
            onVincular={(s) => setModalVincular({ open: true, servico: s })}
            onAdicionar={() => setModalServico({ open: true, servico: null, defaultNaoRelacionado: true })}
            labelBotaoAdicionar="Serviço"
          />
        </div>
      </div>

      {modalServico.open && (
        <ModalServico
          servico={modalServico.servico}
          defaultNaoRelacionado={modalServico.defaultNaoRelacionado}
          onClose={() => setModalServico({ open: false, servico: null })}
          onSaved={handleServicoSalvo}
          onDeleted={handleServicoExcluido}
        />
      )}

      {modalPassivo.open && (
        <ModalPassivoForm
          passivo={modalPassivo.passivo}
          onClose={() => setModalPassivo({ open: false, passivo: null })}
          onSaved={handlePassivoSalvo}
          onDeleted={handlePassivoExcluido}
        />
      )}

      {modalVincular.open && (
        <ModalVincularPassivos
          servico={modalVincular.servico}
          todosPassivos={servicosPassivos}
          onClose={() => setModalVincular({ open: false, servico: null })}
          onSaved={handleVinculosSalvos}
        />
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// CARD REUTILIZÁVEL: LISTA DE SERVIÇOS PRINCIPAIS (veiculares ou não)
// ══════════════════════════════════════════════════════════════
const ColunaServicosPrincipais = ({
  titulo,
  servicos,
  busca,
  onBuscaChange,
  placeholderBusca,
  loading,
  mensagemVazia,
  onEditar,
  onVincular,
  onAdicionar,
  labelBotaoAdicionar
}) => (
  <div style={styles.coluna}>
    <h2 style={styles.colunaTitulo}>{titulo}</h2>

    <div style={styles.buscaBox}>
      <FaSearch style={styles.buscaIcone} />
      <input
        type="text"
        value={busca}
        onChange={(e) => onBuscaChange(e.target.value)}
        placeholder={placeholderBusca}
        style={styles.buscaInput}
      />
    </div>

    <div style={styles.lista}>
      {loading && servicos.length === 0 ? (
        <p style={styles.loading}>Carregando...</p>
      ) : servicos.length > 0 ? (
        servicos.map(s => (
          <div key={s.idServico} style={styles.item}>
            <div style={styles.itemInfo}>
              <div style={styles.itemDescricaoLinha}>
                <span style={styles.itemDescricao}>{s.descricao}</span>
                {s.ativo === false && <span style={styles.badgeInativo}>Inativo</span>}
              </div>
              {s.valor == null ? (
                <span style={styles.badgePendente}>Aguardando cadastro</span>
              ) : (
                <span style={styles.itemValor}>{numeroParaReais(s.valor)}</span>
              )}
            </div>
            <div style={styles.itemAcoes}>
              <button
                title="Editar serviço"
                onClick={() => onEditar(s)}
                style={styles.iconButton}
              >
                <FaEdit />
              </button>
              {/* serviços não relacionados a veículo não têm serviço passivo, então some com o ícone de vincular */}
              {!s.naoRelacionadoVeiculo && (
                <button
                  title="Vincular serviços passivos"
                  onClick={() => onVincular(s)}
                  style={styles.iconButtonLink}
                >
                  <FaLink />
                </button>
              )}
            </div>
          </div>
        ))
      ) : (
        <p style={styles.noResults}>{mensagemVazia}</p>
      )}
    </div>

    <button onClick={onAdicionar} style={styles.addButton}>
      <FaPlus /> {labelBotaoAdicionar}
    </button>
  </div>
);

// ══════════════════════════════════════════════════════════════
// MODAL: EDITAR/CADASTRAR SERVIÇO PRINCIPAL
// ══════════════════════════════════════════════════════════════
const ModalServico = ({ servico, defaultNaoRelacionado, onClose, onSaved, onDeleted }) => {
  const [descricao, setDescricao] = useState(servico?.descricao || '');
  // ── Guardamos o valor como string (formato invariante "1234.5") em vez de float: reatribuir
  // um número puro ao NumericFormat a cada tecla faz o cursor pular pro fim do campo a cada digitação.
  const [valor, setValor] = useState(servico?.valor != null ? String(servico.valor) : '');
  // ── Definido pelo card de origem (veicular ou não); em edição, prevalece o que já está salvo. Não é mais editável no modal.
  const naoRelacionadoVeiculo = servico?.naoRelacionadoVeiculo ?? defaultNaoRelacionado ?? false;
  const [ativo, setAtivo] = useState(servico?.ativo !== false);
  const [errors, setErrors] = useState({ descricao: '', valor: '' });
  const [salvando, setSalvando] = useState(false);
  const [confirmExcluir, setConfirmExcluir] = useState(false);

  const { addToast } = useToast();
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  const validar = () => {
    const errs = { descricao: '', valor: '' };
    if (!descricao) errs.descricao = 'Este campo é obrigatório';
    if (!valor || parseFloat(valor) <= 0) errs.valor = 'Valor deve ser maior que zero';
    setErrors(errs);
    return !errs.descricao && !errs.valor;
  };

  const handleSalvar = async () => {
    if (!validar()) {
      addToastRef.current('warning', 'Preencha todos os campos obrigatórios corretamente!');
      return;
    }
    try {
      setSalvando(true);
      const token = localStorage.getItem('jwtToken');
      // Mantém o mesmo payload já usado pelo back (servicoPassivosIds preserva os vínculos existentes)
      // ── NOVO: campo naoRelacionadoVeiculo. Se marcado, o serviço não usa passivos, então zera o vínculo.
      const dadosParaEnviar = {
        descricao,
        valor: parseFloat(valor) || 0,
        naoRelacionadoVeiculo,
        ativo,
        servicoPassivosIds: naoRelacionadoVeiculo
          ? []
          : (servico?.servicoPassivos?.map(sp => sp.idServicoPassivo) || [])
      };

      let response;
      if (servico?.idServico) {
        response = await axios.put(
          `${API_BASE_URL}/servicos/${servico.idServico}`,
          dadosParaEnviar,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/servicos`,
          dadosParaEnviar,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      }

      addToastRef.current('success', servico?.idServico ? 'Serviço atualizado com sucesso!' : 'Serviço criado com sucesso!');
      onSaved(response.data);
    } catch (err) {
      console.error('Erro ao salvar serviço:', err);
      addToastRef.current('error', err.response?.data?.message || 'Erro ao salvar serviço');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async () => {
    try {
      setSalvando(true);
      const token = localStorage.getItem('jwtToken');
      await axios.delete(`${API_BASE_URL}/servicos/${servico.idServico}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToastRef.current('success', 'Serviço excluído com sucesso!');
      onDeleted(servico.idServico);
    } catch (err) {
      console.error('Erro ao excluir serviço:', err);
      addToastRef.current('error', err.response?.data?.message || 'Erro ao excluir serviço');
    } finally {
      setSalvando(false);
      setConfirmExcluir(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{servico ? 'Editar Serviço' : 'Cadastrar Serviço'}</h2>
          <button onClick={onClose} style={styles.modalClose} disabled={salvando}>&times;</button>
        </div>
        <div style={styles.modalContent}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Descrição*</label>
            <input
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descrição do serviço"
              style={{ ...styles.input, ...(errors.descricao && styles.inputError) }}
              disabled={salvando}
              onBlur={validar}
            />
            {errors.descricao && <span style={styles.errorText}>{errors.descricao}</span>}
          </div>
          <br />
          <div style={styles.inputGroup}>
            <label style={styles.label}>Valor*</label>
            <NumericFormat
              value={valor}
              onValueChange={(values) => setValor(values.value)}
              thousandSeparator="."
              decimalSeparator=","
              placeholder="R$ 0,00"
              decimalScale={2}
              style={{ ...styles.input, ...(errors.valor && styles.inputError) }}
              disabled={salvando}
              onBlur={validar}
            />
            {errors.valor && <span style={styles.errorText}>{errors.valor}</span>}
          </div>
          {naoRelacionadoVeiculo && (
            <>
              <br />
              <span style={styles.avisoNaoVeicular}>
                Este serviço não terá a opção de vincular serviços passivos, e na ordem de serviço não será solicitado placa/dados do veículo.
              </span>
            </>
          )}
          <br />
          <div style={styles.inputGroup}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
                style={styles.checkbox}
                disabled={salvando}
              />
              <span style={styles.servicoDescricao}>Serviço ativo</span>
            </div>
            {!ativo && (
              <span style={styles.avisoNaoVeicular}>
                Serviços inativos não aparecem na lista de seleção ao montar uma Ordem de Serviço, mas continuam válidos em ordens já criadas.
              </span>
            )}
          </div>
        </div>
        <div style={styles.modalFooter}>
          {servico?.idServico && (
            <button onClick={() => setConfirmExcluir(true)} style={styles.deleteButtonModal} disabled={salvando}>
              <FaTrash /> Excluir
            </button>
          )}
          <button onClick={onClose} style={styles.secondaryButton} disabled={salvando}>
            <FaTimes /> Cancelar
          </button>
          <button onClick={handleSalvar} style={styles.primaryButton} disabled={salvando}>
            {salvando ? 'Salvando...' : (<><FaSave /> {servico ? 'Atualizar' : 'Salvar'}</>)}
          </button>
        </div>

        <ConfirmModal
          isOpen={confirmExcluir}
          onClose={() => setConfirmExcluir(false)}
          onConfirm={handleExcluir}
          title="Excluir serviço"
          message="Tem certeza que deseja excluir este serviço? Os vínculos com serviços passivos também serão perdidos."
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// MODAL: EDITAR/CADASTRAR SERVIÇO PASSIVO
// ══════════════════════════════════════════════════════════════
const ModalPassivoForm = ({ passivo, onClose, onSaved, onDeleted }) => {
  const [descricao, setDescricao] = useState(passivo?.descricao || '');
  // ── Valor como string (mesma razão do ModalServico: evita o cursor pular pro fim a cada tecla)
  const [valor, setValor] = useState(passivo?.valor != null ? String(passivo.valor) : '');
  const [valorDigitavel, setValorDigitavel] = useState(passivo?.valorDigitavel || false);
  const [errors, setErrors] = useState({ descricao: '', valor: '' });
  const [salvando, setSalvando] = useState(false);
  const [confirmExcluir, setConfirmExcluir] = useState(false);

  const { addToast } = useToast();
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  // Mantém a mesma lógica atual: quando é digitável, zera e desabilita o campo de valor
  const alterarValorDigitavel = (checked) => {
    setValorDigitavel(checked);
    if (checked) setValor('0');
  };

  const validar = () => {
    const errs = { descricao: '', valor: '' };
    if (!descricao) errs.descricao = 'Este campo é obrigatório';
    if (!valorDigitavel && (!valor || parseFloat(valor) <= 0)) errs.valor = 'Valor deve ser maior que zero';
    setErrors(errs);
    return !errs.descricao && !errs.valor;
  };

  const handleSalvar = async () => {
    if (!validar()) {
      addToastRef.current('warning', 'Preencha todos os campos obrigatórios corretamente!');
      return;
    }
    try {
      setSalvando(true);
      const token = localStorage.getItem('jwtToken');
      const dadosParaEnviar = { descricao, valor: parseFloat(valor) || 0, valorDigitavel };

      let response;
      if (passivo?.idServicoPassivo) {
        response = await axios.put(
          `${API_BASE_URL}/servicoPassivos/${passivo.idServicoPassivo}`,
          dadosParaEnviar,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/servicoPassivos`,
          dadosParaEnviar,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      }

      addToastRef.current('success', passivo ? 'Serviço passivo atualizado com sucesso!' : 'Serviço passivo criado com sucesso!');
      onSaved(response.data);
    } catch (err) {
      console.error('Erro ao salvar serviço passivo:', err);
      addToastRef.current('error', err.response?.data?.message || 'Erro ao salvar serviço passivo');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async () => {
    try {
      setSalvando(true);
      const token = localStorage.getItem('jwtToken');
      await axios.delete(`${API_BASE_URL}/servicoPassivos/${passivo.idServicoPassivo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addToastRef.current('success', 'Serviço passivo excluído com sucesso!');
      onDeleted(passivo.idServicoPassivo);
    } catch (err) {
      console.error('Erro ao excluir serviço passivo:', err);
      addToastRef.current('error', err.response?.data?.message || 'Erro ao excluir serviço passivo');
    } finally {
      setSalvando(false);
      setConfirmExcluir(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{passivo ? 'Editar Serviço Passivo' : 'Cadastrar Serviço Passivo'}</h2>
          <button onClick={onClose} style={styles.modalClose} disabled={salvando}>&times;</button>
        </div>
        <div style={styles.modalContent}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Descrição*</label>
            <input
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descrição do serviço passivo"
              style={{ ...styles.input, ...(errors.descricao && styles.inputError) }}
              disabled={salvando}
              onBlur={validar}
            />
            {errors.descricao && <span style={styles.errorText}>{errors.descricao}</span>}
          </div>
          <br />
          <div style={styles.inputGroup}>
            <label style={styles.label}>Valor*</label>
            <NumericFormat
              value={valor}
              onValueChange={(values) => setValor(values.value)}
              thousandSeparator="."
              decimalSeparator=","
              placeholder="R$ 0,00"
              decimalScale={2}
              style={{ ...styles.input, ...(errors.valor && styles.inputError) }}
              disabled={salvando || valorDigitavel}
              onBlur={validar}
            />
            {errors.valor && <span style={styles.errorText}>{errors.valor}</span>}
          </div>
          <br />
          <div style={styles.inputGroup}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={valorDigitavel}
                onChange={(e) => alterarValorDigitavel(e.target.checked)}
                style={styles.checkbox}
                disabled={salvando}
              />
              <span style={styles.servicoDescricao}>Possui valor digitável</span>
            </div>
          </div>
        </div>
        <div style={styles.modalFooter}>
          {passivo?.idServicoPassivo && (
            <button onClick={() => setConfirmExcluir(true)} style={styles.deleteButtonModal} disabled={salvando}>
              <FaTrash /> Excluir
            </button>
          )}
          <button onClick={onClose} style={styles.secondaryButton} disabled={salvando}>
            <FaTimes /> Cancelar
          </button>
          <button onClick={handleSalvar} style={styles.primaryButton} disabled={salvando}>
            {salvando ? 'Salvando...' : (<><FaSave /> {passivo ? 'Atualizar' : 'Salvar'}</>)}
          </button>
        </div>

        <ConfirmModal
          isOpen={confirmExcluir}
          onClose={() => setConfirmExcluir(false)}
          onConfirm={handleExcluir}
          title="Excluir serviço passivo"
          message="Tem certeza que deseja excluir este serviço passivo?"
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// MODAL: VINCULAR SERVIÇOS PASSIVOS A UM SERVIÇO PRINCIPAL
// ══════════════════════════════════════════════════════════════
const ModalVincularPassivos = ({ servico, todosPassivos, onClose, onSaved }) => {
  const [selecionados, setSelecionados] = useState(
    (servico.servicoPassivos || []).map(sp => sp.idServicoPassivo)
  );
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);

  const { addToast } = useToast();
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;

  const toggle = (id) => {
    setSelecionados(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const passivosFiltrados = todosPassivos.filter(p =>
    p.descricao?.toLowerCase().includes(busca.toLowerCase())
  );

  const handleSalvar = async () => {
    try {
      setSalvando(true);
      const token = localStorage.getItem('jwtToken');
      // Mesmo payload de sempre: descricao + valor do serviço + lista de ids dos passivos
      const dadosParaEnviar = {
        descricao: servico.descricao,
        valor: servico.valor,
        servicoPassivosIds: selecionados
      };
      const response = await axios.put(
        `${API_BASE_URL}/servicos/${servico.idServico}`,
        dadosParaEnviar,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      addToastRef.current('success', 'Serviços passivos vinculados com sucesso!');
      onSaved(response.data);
    } catch (err) {
      console.error('Erro ao vincular serviços passivos:', err);
      addToastRef.current('error', err.response?.data?.message || 'Erro ao vincular serviços passivos');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Vincular Serviços Passivos</h2>
          <button onClick={onClose} style={styles.modalClose} disabled={salvando}>&times;</button>
        </div>
        <div style={styles.modalContent}>
          <p style={styles.vincularSubtitulo}>
            Serviço: <strong>{servico.descricao}</strong>
          </p>

          <div style={styles.searchBox}>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar serviço passivo..."
              style={styles.searchInput}
              disabled={salvando}
            />
          </div>

          <div style={styles.resultsContainer}>
            {passivosFiltrados.length > 0 ? (
              passivosFiltrados.map(p => (
                <label key={p.idServicoPassivo} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selecionados.includes(p.idServicoPassivo)}
                    onChange={() => toggle(p.idServicoPassivo)}
                    style={styles.checkbox}
                    disabled={salvando}
                  />
                  <span style={styles.servicoDescricao}>{p.descricao}</span>
                  <span style={styles.servicoValor}>
                    {p.valorDigitavel ? 'Digitável' : numeroParaReais(p.valor)}
                  </span>
                </label>
              ))
            ) : (
              <p style={styles.noResults}>Nenhum serviço passivo encontrado</p>
            )}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <span style={styles.contadorSelecionados}>{selecionados.length} selecionado(s)</span>
          <button onClick={onClose} style={styles.secondaryButton} disabled={salvando}>
            <FaTimes /> Cancelar
          </button>
          <button onClick={handleSalvar} style={styles.primaryButton} disabled={salvando}>
            {salvando ? 'Salvando...' : (<><FaSave /> Salvar Vínculos</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// ESTILOS
// ══════════════════════════════════════════════════════════════
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%'
  },
  header: {
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    margin: '0',
    color: '#333'
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '20px'
  },

  // Layout em grid: coluna esquerda com 2 cards empilhados (veículos / não veiculares),
  // coluna direita com o card de passivos ocupando a altura das duas linhas
  colunas: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridAutoRows: 'auto',
    gap: '24px',
    alignItems: 'start'
  },
  coluna: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column'
  },
  colunaPassivos: {
    gridColumn: '2',
    gridRow: '1 / span 2'
  },
  colunaTitulo: {
    fontSize: '17px',
    margin: '0 0 16px 0',
    color: '#2c3e50',
    borderBottom: '2px solid #f0f0f0',
    paddingBottom: '12px'
  },

  // Busca
  buscaBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    marginBottom: '16px',
    backgroundColor: '#fafafa'
  },
  buscaIcone: {
    color: '#95a5a6',
    fontSize: '14px'
  },
  buscaInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    flex: 1,
    fontSize: '14px',
    padding: '4px 0'
  },

  // Lista
  lista: {
    maxHeight: '480px',
    overflowY: 'auto',
    marginBottom: '16px'
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 4px',
    borderBottom: '1px solid #f0f0f0',
    gap: '12px'
  },
  itemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0
  },
  itemDescricaoLinha: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0
  },
  itemDescricao: {
    fontSize: '14px',
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  itemValor: {
    fontSize: '12px',
    color: '#2ecc71',
    fontWeight: '600'
  },
  badgePendente: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#b7791f',
    backgroundColor: '#fef3cd',
    padding: '2px 8px',
    borderRadius: '10px',
    alignSelf: 'flex-start'
  },
  badgeInativo: {
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    color: '#7f8c8d',
    backgroundColor: '#ecf0f1',
    padding: '2px 8px',
    borderRadius: '10px',
    flexShrink: 0
  },
  itemAcoes: {
    display: 'flex',
    gap: '6px',
    flexShrink: 0
  },
  iconButton: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#eaf3fb',
    color: '#3498db',
    cursor: 'pointer',
    fontSize: '14px'
  },
  iconButtonLink: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#eafaf1',
    color: '#27ae60',
    cursor: 'pointer',
    fontSize: '14px'
  },

  // Botão de adicionar no rodapé da coluna
  addButton: {
    padding: '12px 20px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: 'auto'
  },

  noResults: {
    color: '#95a5a6',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '20px 0'
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
    color: '#7f8c8d'
  },

  // Formulários (inputs dos modais)
  inputGroup: {
    flex: '1',
    minWidth: '200px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#34495e'
  },
  input: {
    width: '95%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '15px'
  },
  inputError: {
    borderColor: '#ff4444'
  },
  errorText: {
    color: '#ff4444',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  },
  avisoNaoVeicular: {
    display: 'block',
    color: '#8e44ad',
    fontSize: '12px',
    marginTop: '8px',
    fontStyle: 'italic'
  },

  servicoDescricao: {
    flex: '1'
  },
  servicoValor: {
    color: '#2ecc71',
    fontWeight: '600'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    cursor: 'pointer',
    padding: '10px 4px',
    borderBottom: '1px solid #f0f0f0'
  },
  checkbox: {
    marginRight: '10px'
  },

  // Botões
  primaryButton: {
    padding: '12px 20px',
    backgroundColor: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600'
  },
  secondaryButton: {
    padding: '12px 20px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600'
  },
  deleteButtonModal: {
    padding: '12px 20px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    marginRight: 'auto'
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #eee'
  },
  modalTitle: {
    fontSize: '20px',
    margin: 0,
    color: '#333'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#777'
  },
  modalContent: {
    padding: '20px'
  },
  modalFooter: {
    padding: '20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '15px'
  },

  vincularSubtitulo: {
    fontSize: '14px',
    color: '#555',
    marginBottom: '16px'
  },
  contadorSelecionados: {
    fontSize: '13px',
    color: '#7f8c8d',
    marginRight: 'auto'
  },

  searchBox: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px'
  },
  searchInput: {
    flex: '1',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '15px'
  },
  resultsContainer: {
    maxHeight: '400px',
    overflowY: 'auto'
  }
};

export default CadastroServico;
